import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../login/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';

export default function AzureCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = searchParams.get('code');
    const errorParam = searchParams.get('error');
    
    if (errorParam) {
      setError(`Azure authentication failed: ${errorParam}`);
      return;
    }
    
    if (code) {
      // The AuthContext will handle the callback automatically
      // Just wait for it to complete and redirect
      const timer = setTimeout(() => {
        if (isAuthenticated) {
          const from = location.state?.from?.pathname || '/';
          navigate(from, { replace: true });
        }
      }, 2000);
      
      return () => clearTimeout(timer);
    } else {
      setError('No authorization code received from Azure');
    }
  }, [searchParams, navigate, isAuthenticated]);

  // Redirect when authenticated
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
          <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-lg font-medium text-gray-900 text-center mb-2">
            Authentication Error
          </h2>
          <p className="text-sm text-gray-600 text-center mb-6">
            {error}
          </p>
          <button
            onClick={() => navigate('/login')}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
        <LoadingSpinner text="Completing Azure authentication..." />
        <p className="text-sm text-gray-600 text-center mt-4">
          Please wait while we complete your authentication...
        </p>
      </div>
    </div>
  );
}
